import classnames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import { prettyDate, prettyFloatMoney } from '@/helpers/utils';
import { Preloader } from '@/components/Preloader';
import { EmptyContent } from '@/components/EmptyContent';
import {
  DATE_FORMAT_DD_MMM_YYYY,
} from '@/helpers/common';
import { ProgressBar } from '@/components/ProgressBar';
import { MyTooltip } from '@/webkit/MyTooltip';
import IconTooltip from '../info-circle.svg';
import IconView from './icon_file.svg';
import s from './index.module.scss';

export const Table = ({ data, isLoading, onLoanOpen }) => {
  const { t } = useTranslation('loans');

  return (
    <div className={s.table_wrapper}>
      <div className={s.table_inner}>
        <div className={s.table}>
          <div className={s.table__thead}>
            <div className={s.table__thead_col}>{t('round')}</div>
            <div className={s.table__thead_col}>{t('origination_date')}</div>
            <div className={s.table__thead_col}>{t('loan_amount')}</div>
            <div className={s.table__thead_col}>{t('repayment_progress')}</div>
            <div className={s.table__thead_col}>{t('repaid')}</div>
            <div
              className={s.table__thead_col}
              style={{ textAlign: 'right', paddingRight: '44px' }}
            >
              {t('your_earnings')}
            </div>
            <div className={s.table__thead_col} />
          </div>

          {isLoading ? (
            <Preloader className={s.preloader} />
          ) : data?.total_count > 0 ? (
            <div className={s.table__tbody}>
              {data.list.map(round => {
                const {
                  fundraising_name,
                  fundraising_id,
                  start_date,
                  end_date,
                  max_earnings,
                  loan_amount,
                  repayment_status,
                  repaid_amount,
                  earnings,
                  flat_fee_accrued,
                } = round;

                const showEarnings =
                  fundraising_id > 92 && repayment_status < 100;

                return (
                  <div
                    className={classnames(s.table__tbody_row, 'hov')}
                    key={fundraising_name}
                    onClick={() => onLoanOpen(round)}
                  >
                    <div className={s.table__tbody_col}>
                      <span className="f-small-accent">{fundraising_name}</span>
                    </div>
                    <div className={s.table__tbody_col}>
                      <span className="f-small-regular">
                        {prettyDate(start_date, DATE_FORMAT_DD_MMM_YYYY)}
                      </span>
                    </div>
                    <div className={s.table__tbody_col}>
                      <span className="f-small-regular tl">
                        {prettyFloatMoney(loan_amount, {
                          decimalPlaces: 2,
                        })}
                      </span>
                    </div>
                    <div className={s.table__tbody_col}>
                      <ProgressBar value={repayment_status} />
                    </div>
                    <div className="loans_table__table__tbody_col tl">
                      <span className="f-small-regular">
                        {prettyFloatMoney(repaid_amount, {
                          decimalPlaces: 2,
                        })}
                      </span>
                    </div>
                    <div className={classnames(s.table__tbody_col)}>
                      {showEarnings ? (
                        <div className={s.earnings}>
                          <div className={s.earnings_plus}>
                            <span className="f-small-regular">
                              {prettyFloatMoney(earnings + flat_fee_accrued, {
                                decimalPlaces: 2,
                              })}
                            </span>
                            <span className="f-small-regular">
                              {t('out_of', {
                                amount: prettyFloatMoney(max_earnings, {
                                  decimalPlaces: 2,
                                }),
                              })}
                            </span>
                          </div>
                          <div>
                            <MyTooltip
                              theme="dark"
                              placement="left"
                              renderContent={() => (
                                <div
                                  className="f-16 f-400"
                                  style={{ maxWidth: '350px' }}
                                >
                                  <Trans
                                    ns="loans"
                                    i18nKey="tooltip_two_key_values"
                                    values={{
                                      earnings: prettyFloatMoney(
                                        earnings + flat_fee_accrued,
                                        {
                                          decimalPlaces: 2,
                                        },
                                      ),
                                      max_earnings: prettyFloatMoney(
                                        max_earnings,
                                        {
                                          decimalPlaces: 2,
                                        },
                                      ),
                                      end_date: prettyDate(
                                        end_date,
                                        'DD.MM.YYYY',
                                      ),
                                    }}
                                    components={{
                                      span: <span className="f-500" />,
                                      br: <br />,
                                    }}
                                  />
                                </div>
                              )}
                              renderElement={() => (
                                <img
                                  src={IconTooltip}
                                  alt="tooltip"
                                  width={16}
                                  height={16}
                                  style={{ minWidth: '16px' }}
                                />
                              )}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className={s.earnings}>
                          <span className="f-small-regular">
                            +{' '}
                            {prettyFloatMoney(earnings, {
                              decimalPlaces: 2,
                            })}
                          </span>
                          <div style={{ minWidth: '16px' }} />
                        </div>
                      )}
                    </div>
                    <div className={classnames(s.table__tbody_col)}>
                      <MyTooltip
                        theme="dark"
                        renderElement={() => (
                          <button
                            className={s.btn_loan}
                            type="button"
                            onClick={() => null}
                          >
                            <img
                              src={IconView}
                              alt="view"
                              width={20}
                              height={20}
                            />
                          </button>
                        )}
                        renderContent={() => (
                          <span className="f-small-regular">
                            {t('details')}
                          </span>
                        )}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={s.empty}>
              <EmptyContent
                title={t('no_claims_yet')}
                text={t('your_claims_will_appear_here')}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
